import React, { useEffect, useRef, useState } from 'react';
import KegScanOverlay from '../KegScanOverlay';
import { primeScanAudio } from '../../utils/scanFeedback';
import { Batch, Location } from '../../types/interfaces';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import {
  adjustVolumeAndPackage,
  checkKegForPackaging,
  ProductPackageChoice,
  choiceLabel,
  findBatchProduct,
  postBatchPackaging,
  productPackageChoices,
} from '../../utils/batchPackaging';

interface Props {
  batch: Batch;
  fromLabel: string;
  locationId: number;
  locations: Location[];
  onCancel: () => void;
  onDone: (message: string) => void;
}

/**
 * Drop a batch on a storage location → the existing packaging flow (same
 * POST /api/batches/:id/package, same keg rules via utils/batchPackaging).
 */
const BatchPackagingModal: React.FC<Props> = ({ batch, fromLabel, locationId, locations, onCancel, onDone }) => {
  const [packageTypes, setPackageTypes] = useState<{ name: string; volume: number; enabled: number }[]>([]);
  const [choices, setChoices] = useState<ProductPackageChoice[] | null>(null);
  const [productId, setProductId] = useState<string | number | null>(batch.productId ?? null);
  const [packageType, setPackageType] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [location, setLocation] = useState(String(locationId));
  const [kegCodes, setKegCodes] = useState<string[]>([]);
  const [kegInput, setKegInput] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  // Latest list for camera scans (they can land before a re-render).
  const kegCodesRef = useRef<string[]>(kegCodes);
  kegCodesRef.current = kegCodes;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shortfall, setShortfall] = useState<{ message: string; shortfall: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ptRes, prodRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/package-types`, { headers: { Accept: 'application/json' } }),
          apiFetch(`${API_BASE_URL}/api/products`, { headers: { Accept: 'application/json' } }),
        ]);
        const pts = ptRes.ok ? await ptRes.json() : [];
        const prods = prodRes.ok ? await prodRes.json() : [];
        if (cancelled) return;
        const ptList = Array.isArray(pts) ? pts : [];
        const prod: any = findBatchProduct(Array.isArray(prods) ? prods : [], batch);
        setPackageTypes(ptList);
        setProductId(prod?.id ?? batch.productId ?? null);
        setChoices(productPackageChoices(prod?.packageTypes, ptList));
      } catch (err: any) {
        if (!cancelled) setError('Failed to load package types: ' + err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batch.productId, batch.productName]); // eslint-disable-line react-hooks/exhaustive-deps

  void packageTypes;
  const keg = (choices || []).some((c) => c.type === packageType && c.keg);
  const kegsOk = !keg || kegCodes.length === quantity;

  const addKeg = async () => {
    const { code, error: kegError } = await checkKegForPackaging(kegInput, kegCodes);
    if (!code) return setError(kegError || 'Failed to add keg');
    setKegCodes((k) => (k.includes(code) ? k : [...k, code]));
    setKegInput('');
    setError(null);
  };

  /** Camera scan (full-screen scanner): same keg-tracking checks as manual entry; throws to reject. */
  const addScannedKeg = async (raw: string) => {
    const existing = kegCodesRef.current;
    if (quantity > 0 && existing.length >= quantity) {
      throw new Error(`Already have ${quantity} keg(s) for ${quantity} ${packageType}`);
    }
    const { code, error: kegError } = await checkKegForPackaging(raw, existing);
    if (!code) throw new Error(kegError || 'Failed to add keg');
    kegCodesRef.current = existing.includes(code) ? existing : [...existing, code];
    setKegCodes((k) => (k.includes(code) ? k : [...k, code]));
    setError(null);
  };

  const req = () => ({ packageType, quantity, locationId: location || null, kegCodes: keg ? kegCodes : [] });

  const submit = async () => {
    if (!packageType || quantity <= 0 || !location) return setError('Please select a package type, quantity (> 0), and location');
    if (!kegsOk) return setError(`Reconcile keg assets: scan or enter exactly ${quantity} keg serials for ${packageType}`);
    setBusy(true);
    setError(null);
    try {
      const data = await postBatchPackaging(batch.batchId, req());
      if (data.prompt === 'volumeAdjustment') {
        setShortfall({ message: data.message, shortfall: data.shortfall });
        return;
      }
      onDone(data.message || `Packaged ${quantity} × ${packageType}`);
    } catch (err: any) {
      setError('Failed to package: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const adjust = async () => {
    if (!shortfall) return;
    setBusy(true);
    try {
      const data = await adjustVolumeAndPackage(batch.batchId, Number(batch.volume ?? 0), Number(shortfall.shortfall), req());
      onDone(data.message || 'Packaged successfully after volume adjustment');
    } catch (err: any) {
      setError('Failed to adjust volume or package: ' + err.message);
      setShortfall(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facility-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pkg-title" data-testid="packaging-modal">
      <div className="facility-modal">
        <h3 id="pkg-title">Package {batch.batchId}</h3>
        <p className="facility-modal__meta">
          <strong>{batch.productName || 'Product'}</strong> from {fromLabel}
        </p>
        <label htmlFor="pkg-type">Package type</label>
        <select id="pkg-type" data-testid="pkg-type" value={packageType} onChange={(e) => { setPackageType(e.target.value); setKegCodes([]); }}>
          <option value="">Select package type</option>
          {(choices || []).map((c) => (
            <option key={c.type} value={c.type}>{choiceLabel(c)}</option>
          ))}
        </select>
        {choices && choices.length === 0 && (
          <p className="facility-modal__warn" data-testid="pkg-no-types">
            {batch.productName || 'This product'} has no package types set up.{' '}
            <a href={productId != null ? `/products/${productId}` : '/products'}>Add them in product setup</a> to package it.
          </p>
        )}
        <label htmlFor="pkg-qty">Quantity</label>
        <input
          id="pkg-qty"
          data-testid="pkg-qty"
          type="number"
          inputMode="numeric"
          min="0"
          value={quantity || ''}
          onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 0)}
        />
        <label htmlFor="pkg-location">Location</label>
        <select id="pkg-location" data-testid="pkg-location" value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="">Select location</option>
          {locations.map((l) => (
            <option key={l.locationId} value={l.locationId}>{l.name}</option>
          ))}
        </select>
        {keg && (
          <div className="facility-modal__kegs" data-testid="pkg-keg-section">
            <label htmlFor="pkg-keg">
              Scan kegs ({kegCodes.length}/{quantity || 0})
            </label>
            <div className="facility-modal__keg-row">
              <input
                id="pkg-keg"
                data-testid="pkg-keg-input"
                value={kegInput}
                onChange={(e) => setKegInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addKeg();
                  }
                }}
                placeholder="Scan or enter keg code"
                autoComplete="off"
              />
              <button type="button" data-testid="pkg-keg-add" onClick={addKeg}>
                Add keg
              </button>
              <button
                type="button"
                data-testid="pkg-keg-camera"
                aria-pressed={showCamera}
                onClick={() => {
                  primeScanAudio(); // iOS: sound needs this tap
                  setShowCamera(true);
                }}
              >
                Scan with camera
              </button>
            </div>
            {showCamera && (
              <KegScanOverlay
                title={`Package ${batch.batchId}`}
                subtitle={`${batch.productName || 'Product'} · ${packageType}`}
                codes={kegCodes}
                target={quantity > 0 ? quantity : undefined}
                onScan={addScannedKeg}
                onRemove={(c) => setKegCodes((k) => k.filter((x) => x !== c))}
                onDone={() => setShowCamera(false)}
              />
            )}
            {kegCodes.length > 0 && (
              <ul className="facility-modal__keg-list" data-testid="pkg-keg-list">
                {kegCodes.map((c) => (
                  <li key={c}>
                    {c}{' '}
                    <button type="button" aria-label={`Remove ${c}`} onClick={() => setKegCodes((k) => k.filter((x) => x !== c))}>
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {shortfall && (
          <div className="facility-modal__warn" data-testid="pkg-shortfall" role="dialog" aria-label="Adjust brite WIP?">
            <p>{shortfall.message}</p>
            <button type="button" className="is-primary" data-testid="pkg-shortfall-yes" onClick={adjust} disabled={busy}>
              Yes
            </button>{' '}
            <button
              type="button"
              className="is-secondary"
              data-testid="pkg-shortfall-no"
              disabled={busy}
              onClick={() => {
                setShortfall(null);
                setError('Packaging cancelled due to insufficient volume');
              }}
            >
              No / Cancel
            </button>
          </div>
        )}
        {error && (
          <div className="facility-modal__error" role="alert" data-testid="pkg-error">
            {error}
          </div>
        )}
        <div className="facility-modal__actions">
          <button
            type="button"
            className="is-primary"
            data-testid="pkg-confirm"
            onClick={submit}
            disabled={busy || !!shortfall || !packageType || quantity <= 0 || !kegsOk}
          >
            {busy ? 'Packaging…' : keg && !kegsOk ? `Scan ${quantity - kegCodes.length} more keg(s)` : 'Package'}
          </button>
          <button type="button" className="is-secondary" data-testid="pkg-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchPackagingModal;
